import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { StaticNetworkMap as NetworkMap } from './StaticNetworkMap';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Demographics } from '../data';
import { CreativeSummary, CreativeDetailCampaign, CreativeDetailScreen, HourlyAveragePoint } from '../adapters/campaigns';
import { fmtFull } from '../utils/formatters';

// Same brand theme as PopReport.tsx (campaign-level export), kept local since
// neither file exports it — duplicating a handful of style constants here
// keeps the two report exports decoupled and independently editable.
const THEME = {
  sheetBg: '#F7F8FA',
  panelBg: '#ffffff',
  panelBorder: '1px solid #E6E9ED',
  panelShadow: '0 1px 2px rgba(15,23,42,0.03)',
  trackBg: '#EEF1F3',
  green: '#4ADE80',
  greenDeep: '#0a6e57',
  greenDark: '#00783A',
  navy: '#0A1828',
  textStrong: '#0B1220',
  text: '#1e293b',
  textMuted: '#64748b',
  textFaint: '#94a3b8',
};

interface MapScreen {
  id: string;
  name: string;
  venue?: string;
  lat: number;
  lon: number;
}

interface CreativeReportProps {
  creative: CreativeSummary;
  startDate: string;
  endDate: string;
  campaigns: CreativeDetailCampaign[];
  screens: CreativeDetailScreen[];
  mapScreens: MapScreen[];
  hourlyAverage: HourlyAveragePoint[];
  totalDemographics: Demographics | null;
  onClose: () => void;
}

function fmtDate(s: string, includeYear = true): string {
  if (!s) return '';
  return new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: includeYear ? 'numeric' : undefined });
}

// "Aug 5 - Aug 11, 2026" when both ends share a year (dropping the
// redundant year off the start), "Dec 28, 2025 - Jan 3, 2026" when they
// don't, or "Aug 5, 2026 - present" for an open-ended flight.
function fmtDateRange(startDate: string, endDate: string): string {
  if (!startDate) return '';
  if (!endDate) return `${fmtDate(startDate)} - present`;
  const sameYear = new Date(startDate + 'T00:00:00').getFullYear() === new Date(endDate + 'T00:00:00').getFullYear();
  return `${fmtDate(startDate, !sameYear)} - ${fmtDate(endDate)}`;
}

function filenameFromUrl(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    return decodeURIComponent(url.split('?')[0].split('/').pop() ?? '') || undefined;
  } catch {
    return url.split('?')[0].split('/').pop() || undefined;
  }
}

function groupByLocation(screens: CreativeDetailScreen[]): { key: string; plays: number; impressions: number; sensorEnabled: boolean }[] {
  const map = new Map<string, { key: string; plays: number; impressions: number; sensorEnabled: boolean }>();
  for (const s of screens) {
    // Some screens come back from the API with no address and no name; fall
    // back to the screen id rather than let a null key blow up downstream
    // (Map keys, React keys, and the px venue matcher all expect a string).
    const key = (s.address && s.address.trim()) || s.name || s.id;
    const existing = map.get(key) ?? { key, plays: 0, impressions: 0, sensorEnabled: false };
    existing.plays += s.plays ?? 0;
    existing.impressions += s.impressions ?? 0;
    existing.sensorEnabled = existing.sensorEnabled || s.sensorEnabled;
    map.set(key, existing);
  }
  return Array.from(map.values());
}

function formatHourLabel(h: number): string {
  const period = h < 12 ? 'a' : 'p';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}${period}`;
}

function hourLabelFull(h: number): string {
  const period = h < 12 ? 'AM' : 'PM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh} ${period}`;
}

// Hour-of-day bar chart — dark bars with the single peak hour highlighted in
// brand green and its value called out above it, matching the "When the
// audience showed up" chart in the client-facing template. Plots the true
// per-day average impressions for each hour (from the backend's hourly
// average endpoint).
function HourlyBarChart({ data }: { data: { hour: string; impressions: number }[] }) {
  // Sized to match the chart panel's actual rendered box (roughly 424x252 at
  // the report's 720px width — chart column width minus the map column and
  // panel padding, by the row's ~280px height minus panel padding) so
  // preserveAspectRatio="none" below scales uniformly instead of squashing
  // the chart horizontally while stretching it vertically.
  const W = 424, H = 252;
  const pad = { top: 34, right: 8, bottom: 22, left: 8 };
  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;
  const n = data.length;
  const gap = 3;
  const barW = (innerW - gap * (n - 1)) / Math.max(n, 1);
  const maxV = Math.max(1, ...data.map(d => d.impressions)) * 1.12;
  const x = (i: number) => pad.left + i * (barW + gap);
  const barH = (v: number) => (v / maxV) * innerH;

  const peakIdx = data.reduce((best, d, i) => (d.impressions > data[best].impressions ? i : best), 0);
  const peak = data[peakIdx];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block' }}>
      <line x1={pad.left} y1={pad.top + innerH} x2={W - pad.right} y2={pad.top + innerH} stroke="#E9ECEF" strokeWidth={1} />
      {data.map((d, i) => {
        const h = Math.max(barH(d.impressions), 2);
        const isPeak = i === peakIdx && d.impressions > 0;
        return (
          <rect
            key={i}
            x={x(i)}
            y={pad.top + innerH - h}
            width={barW}
            height={h}
            rx={1.5}
            fill={isPeak ? THEME.green : THEME.navy}
          />
        );
      })}
      {peak && peak.impressions > 0 && (
        <g>
          <rect x={x(peakIdx) + barW / 2 - 22} y={pad.top - 22} width={44} height={17} rx={5} fill={THEME.green} />
          <text x={x(peakIdx) + barW / 2} y={pad.top - 10} fontSize="10" fontWeight={800} fill="#fff" textAnchor="middle">
            {fmtFull(Math.round(peak.impressions))}
          </text>
        </g>
      )}
      {data.map((d, i) => (
        <text key={i} x={x(i) + barW / 2} y={H - 4} fontSize="7" fill={THEME.textFaint} textAnchor="middle">
          {d.hour}
        </text>
      ))}
    </svg>
  );
}

export function CreativeReport({ creative, startDate, endDate, campaigns, screens, mapScreens, hourlyAverage, totalDemographics, onClose }: CreativeReportProps) {
  const isVideo = creative.type === 'video' || /\.(mp4|mov|webm)$/i.test(creative.creativeUrl ?? '');
  const filename = filenameFromUrl(creative.creativeUrl);
  const displayName = creative.name ?? filename ?? 'Creative';
  const flight = fmtDateRange(startDate, endDate);

  // Presentable overrides for the auto-derived title (usually just the
  // uploaded filename, e.g. "1c99f1bb-....png") and campaign name — set from
  // the export overlay's toolbar. Blank means "use the auto-derived value".
  const [titleOverride, setTitleOverride] = useState('');
  const [campaignNameOverride, setCampaignNameOverride] = useState('');
  const effectiveTitle = titleOverride.trim() || displayName;

  const totalPlays = campaigns.reduce((sum, c) => sum + c.plays, 0) || creative.plays || 0;
  const totalImpressions = campaigns.reduce((sum, c) => sum + c.impressions, 0) || creative.impressions || 0;
  const avgImprPerPlay = totalPlays ? totalImpressions / totalPlays : null;

  // Distinct venues/addresses — used only for the "N screens across M
  // locations" counts; the ranked list below is per-screen.
  const locationsGrouped = useMemo(() => groupByLocation(screens), [screens]);
  const screensByImpressions = useMemo(() => [...screens].sort((a, b) => b.impressions - a.impressions), [screens]);
  const topScreens = screensByImpressions.slice(0, 5);
  const maxScreenImpressions = Math.max(...topScreens.map(s => s.impressions), 1);

  const [mapFailed, setMapFailed] = useState(false);
  const showMap = !mapFailed && mapScreens.some(s => s.lat && s.lon);

  // True per-day average delivery by hour-of-day, from the backend's hourly
  // average endpoint (sum/day_count) — bucketed into a canonical 24-slot
  // array keyed by hour_of_day, since the backend can skip hours with zero
  // delivery (overnight, etc.) and the bar chart below assumes 24
  // evenly-spaced, correctly-labeled hours.
  const hourlyData = useMemo(() => {
    const buckets = Array.from({ length: 24 }, () => ({ impressions: 0, plays: 0 }));
    for (const d of hourlyAverage) {
      const h = d.hour_of_day;
      if (h < 0 || h > 23) continue;
      buckets[h].impressions = d.average_impressions ?? 0;
      buckets[h].plays = d.average_plays ?? 0;
    }
    return buckets.map((b, h) => ({ hour: formatHourLabel(h), hourOfDay: h, impressions: b.impressions, plays: b.plays }));
  }, [hourlyAverage]);
  const peakHour = useMemo(() => {
    const withData = hourlyData.filter(h => h.impressions > 0);
    return withData.length ? withData.reduce((a, b) => (b.impressions > a.impressions ? b : a)) : null;
  }, [hourlyData]);
  // Sums across the average-per-day buckets (not the flight's real totals,
  // which live on a different scale).
  const sumHourlyAverageImpressions = hourlyData.reduce((a, h) => a + h.impressions, 0);
  const overallHourlyAverage = sumHourlyAverageImpressions / 24;

  // True per-day average impressions, spread evenly across every screen
  // running the flight — a simple per-screen productivity figure built from
  // the same per-day average basis as the hourly chart above.
  const avgImpressionsPerScreenPerDay = screens.length ? sumHourlyAverageImpressions / screens.length : null;

  const topCampaign = campaigns.length ? [...campaigns].sort((a, b) => b.impressions - a.impressions)[0] : null;
  const effectiveCampaignName = campaignNameOverride.trim() || topCampaign?.name || '';

  const leadGender = totalDemographics?.gender.length ? [...totalDemographics.gender].sort((a, b) => b.share - a.share)[0] : null;
  const leadAge = totalDemographics?.age.length ? [...totalDemographics.age].sort((a, b) => b.share - a.share)[0] : null;
  const maxAgeShare = totalDemographics?.age.length ? Math.max(...totalDemographics.age.map(a => a.share)) : 1;

  const subtitle = [
    flight || null,
    campaigns.length ? `${fmtFull(campaigns.length)} campaign${campaigns.length === 1 ? '' : 's'}` : null,
    screens.length ? `${fmtFull(screens.length)} screen${screens.length === 1 ? '' : 's'}` : null,
    locationsGrouped.length ? `${fmtFull(locationsGrouped.length)} location${locationsGrouped.length === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' • ');

  const sheetRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const PAGE_W_IN = 8.5;
  const slug = (s: string) => s.trim().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '') || 'report';

  const handleExport = async () => {
    const el = sheetRef.current;
    if (!el || exporting) return;
    setExporting(true);
    const prevRadius = el.style.borderRadius;
    const prevShadow = el.style.boxShadow;
    el.style.borderRadius = '0';
    el.style.boxShadow = 'none';
    try {
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: THEME.sheetBg, useCORS: true });
      const img = canvas.toDataURL('image/png');
      const imgRatio = canvas.width / canvas.height;
      const drawW = PAGE_W_IN;
      const drawH = drawW / imgRatio;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: [PAGE_W_IN, drawH] });
      pdf.addImage(img, 'PNG', 0, 0, drawW, drawH, undefined, 'FAST');
      pdf.save(`${slug(effectiveTitle)}_Creative-Report.pdf`);
    } finally {
      el.style.borderRadius = prevRadius;
      el.style.boxShadow = prevShadow;
      setExporting(false);
    }
  };

  const content = (
    <div id="creative-report-overlay" style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(3,8,14,0.7)', overflowY: 'auto', padding: '32px 16px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto 14px', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <label style={toolbarLabelStyle}>
            Title
            <input
              type="text"
              value={titleOverride}
              onChange={e => setTitleOverride(e.target.value)}
              placeholder={displayName}
              style={toolbarInputStyle}
            />
          </label>
          <label style={toolbarLabelStyle}>
            Campaign Name
            <input
              type="text"
              value={campaignNameOverride}
              onChange={e => setCampaignNameOverride(e.target.value)}
              placeholder={topCampaign?.name || 'Optional'}
              style={toolbarInputStyle}
            />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            Close
          </button>
          <button onClick={handleExport} disabled={exporting} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--brand-green-500)', color: 'var(--brand-navy-900)', fontSize: 12.5, fontWeight: 800, cursor: exporting ? 'default' : 'pointer', fontFamily: 'inherit', opacity: exporting ? 0.7 : 1 }}>
            {exporting ? 'Generating…' : 'Download PDF'}
          </button>
        </div>
      </div>

      <div ref={sheetRef} id="creative-report-sheet" style={{ width: 720, maxWidth: '100%', margin: '0 auto', background: THEME.sheetBg, borderRadius: 16, boxShadow: '0 30px 80px rgba(0,0,0,0.55)', padding: 26, display: 'flex', flexDirection: 'column', gap: 20, fontSize: 12, color: THEME.text }}>

        {/* Header: wordmark */}
        <div>
          <img src="/logo-light.svg" alt="Smartstreet" style={{ height: 46 }} />
          <div style={{ height: 1, background: '#E5E9EC', marginTop: 16 }} />
        </div>

        {/* Creative title + hero impressions figure, hero thumbnail alongside both.
            alignItems: 'stretch' lets the left column match the (taller) hero
            image's height, and the impressions figure below is flex: 1 so it
            grows to fill whatever gap that leaves between the title block and
            the stat row, rather than leaving dead space at the bottom. */}
        <div style={{ display: 'grid', gridTemplateColumns: !isVideo && creative.creativeUrl ? '1fr 150px' : '1fr', gap: 20, alignItems: 'stretch' }}>
          <div style={{ paddingTop: 4, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: THEME.textMuted, marginBottom: 6 }}>Proof of Play</div>
              <div style={{
                fontSize: 26, fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.01em', color: THEME.textStrong,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'break-word',
              }}>
                {effectiveTitle}
              </div>
              {effectiveCampaignName && (
                <div style={{ fontSize: 11.5, fontWeight: 700, color: THEME.greenDeep, marginTop: 4 }}>{effectiveCampaignName}</div>
              )}
              {subtitle && <div style={{ fontSize: 11.5, color: THEME.textMuted, marginTop: 6 }}>{subtitle}</div>}
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', minHeight: 14, width: '100%' }}>
              {/* Stacked, not side-by-side, so the "Impressions" label always
                  lands on its own line under the figure regardless of how
                  wide the number is. */}
              <div style={{ width: '100%' }}>
                <div style={{ fontSize: 84, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em', color: THEME.green, fontVariantNumeric: 'tabular-nums', width: '100%' }}>
                  {fmtFull(totalImpressions)}
                </div>
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: THEME.textStrong }}>Impressions</div>
                  <div style={{ fontSize: 11, color: THEME.textMuted, marginTop: 2 }}>verified audience delivered across the flight</div>
                </div>
              </div>
            </div>
          </div>

          {!isVideo && creative.creativeUrl && (
            <img
              src={creative.creativeUrl}
              crossOrigin="anonymous"
              alt="Creative preview"
              style={{ width: 150, flexShrink: 0, alignSelf: 'start', aspectRatio: '9 / 16', objectFit: 'cover', borderRadius: 14, border: THEME.panelBorder, display: 'block' }}
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          )}
        </div>

        {/* Stat row */}
        <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          {[
            { value: fmtFull(totalPlays), label: 'Plays' },
            { value: avgImprPerPlay != null ? avgImprPerPlay.toFixed(1) : '—', label: 'Average Impressions per Play' },
            { value: fmtFull(screens.length), label: 'Screens' },
            { value: fmtFull(locationsGrouped.length), label: 'Locations' },
          ].map((s, i) => (
            <React.Fragment key={s.label}>
              {i > 0 && <div style={{ width: 1, background: '#E6E9ED', alignSelf: 'stretch' }} />}
              <Stat value={s.value} label={s.label} />
            </React.Fragment>
          ))}
        </div>

        {/* Average Delivery, Hour by Hour, + where it ran */}
        <div>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: THEME.textStrong, marginBottom: 10 }}>Average Delivery, Hour by Hour</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 12 }}>
            <div style={{ ...panelStyle, display: 'flex', flexDirection: 'column' }}>
              {hourlyData.some(h => h.impressions > 0) ? (
                <HourlyBarChart data={hourlyData} />
              ) : (
                <div style={{ fontSize: 11, color: THEME.textFaint, padding: '20px 0' }}>Not enough hourly delivery data collected yet for this creative.</div>
              )}
            </div>
            {showMap ? (
              <NetworkMap
                screens={mapScreens}
                width={200}
                height={280}
                dot={12}
                pad={22}
                fill
                minHeight={280}
                showNote={false}
                onFail={() => setMapFailed(true)}
                style={{ border: THEME.panelBorder }}
              />
            ) : (
              // Placeholder tile for local dev — getCreativeScreens is
              // currently coming back empty (backend access-resolution
              // issue with campaign_uuids omitted), so there's no real
              // lat/lon to plot yet. Swap back to the real map once that's
              // fixed; keeping the slot filled here so the two-column
              // layout stays visible for layout review in the meantime.
              <div style={{
                width: '100%', height: '100%', minHeight: 280, borderRadius: 12,
                border: `1px dashed #C7CDD4`, background: THEME.trackBg,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
              }}>
                <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: THEME.textFaint }}>Map</span>
                <span style={{ fontSize: 8.5, color: THEME.textFaint, textAlign: 'center', padding: '0 10px' }}>Screen locations placeholder</span>
              </div>
            )}
          </div>

          {hourlyData.some(h => h.impressions > 0) && (peakHour || avgImpressionsPerScreenPerDay != null) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              {peakHour && overallHourlyAverage > 0 && (
                <InsightChip value={`${(peakHour.impressions / overallHourlyAverage).toFixed(1)}x`} text={<>The {peakHour.hour.toUpperCase()} hour averages <strong style={{ color: THEME.textStrong }}>{fmtFull(Math.round(peakHour.impressions))} impressions</strong> a day, {(peakHour.impressions / overallHourlyAverage).toFixed(1)}x the hourly average.</>} />
              )}
              {avgImpressionsPerScreenPerDay != null && (
                <InsightChip value={fmtNum(Math.round(avgImpressionsPerScreenPerDay))} text={<>Each screen delivered an average of <strong style={{ color: THEME.textStrong }}>{fmtFull(Math.round(avgImpressionsPerScreenPerDay))} impressions</strong> per day.</>} />
              )}
            </div>
          )}
        </div>

        {/* Top screens + demographics — both cards stretch to the row's full
            height (whichever side is naturally taller), so they read as a
            matched pair rather than the demographics card looking taller
            just because it has more content. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22, alignItems: 'stretch' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={sectionLabelStyle}>Top Screens - Share of Total {fmtNum(totalImpressions)} Impressions</div>
            <div style={{ ...panelStyle, marginTop: 10, padding: 14, flex: 1 }}>
              {topScreens.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {topScreens.map(s => {
                    const pct = (s.impressions / maxScreenImpressions) * 100;
                    return (
                      <div key={s.id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, color: THEME.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{s.address || s.name}</span>
                          <span style={{ fontWeight: 800, color: THEME.greenDeep, flexShrink: 0 }}>{fmtNum(s.impressions)}</span>
                        </div>
                        <div style={{ height: 6, background: THEME.trackBg, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.max(pct, 3)}%`, height: '100%', background: THEME.green, borderRadius: 3 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: THEME.textFaint }}>No screen data yet</div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={sectionLabelStyle}>Who Was in Front of the Screens</div>
            <div style={{ ...panelStyle, marginTop: 10, padding: 14, flex: 1 }}>
              {totalDemographics ? (
                <>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {totalDemographics.gender.map((g, i) => (
                      <div key={g.label} style={{ flex: 1, background: i % 2 === 0 ? THEME.greenDark : THEME.navy, borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>{g.share}%</div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.85)', marginTop: 1 }}>{g.label}</div>
                      </div>
                    ))}
                  </div>
                  {leadGender && <div style={{ fontSize: 9.5, color: THEME.textMuted, marginTop: 8 }}>Audience skewed {leadGender.label.toLowerCase()} ({leadGender.share}%).</div>}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 12 }}>
                    {totalDemographics.age.map((a, i) => (
                      <div key={a.band} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 34px', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 9.5, color: THEME.textMuted }}>{a.band}</span>
                        <div style={{ height: 9, background: THEME.trackBg, borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.max((a.share / maxAgeShare) * 100, 3)}%`, height: '100%', background: i < totalDemographics.age.length / 2 ? THEME.green : THEME.navy, borderRadius: 4 }} />
                        </div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, textAlign: 'right', color: THEME.textStrong }}>{a.share}%</span>
                      </div>
                    ))}
                  </div>
                  {leadAge && <div style={{ fontSize: 9.5, color: THEME.textMuted, marginTop: 8 }}>Led by the {leadAge.band} age band ({leadAge.share}%).</div>}
                </>
              ) : (
                <div style={{ fontSize: 11, color: THEME.textFaint }}>Not enough sensor data collected yet for this creative.</div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );

  return createPortal(content, document.body);
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div style={{ fontSize: 26, fontWeight: 800, color: THEME.textStrong, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: THEME.textMuted, marginTop: 3 }}>{label}</div>
    </div>
  );
}

function InsightChip({ value, text }: { value: string; text: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: THEME.panelBg, border: THEME.panelBorder, borderLeft: `4px solid ${THEME.green}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: THEME.green, flexShrink: 0 }}>{value}</div>
      <div style={{ fontSize: 10.5, color: THEME.text, lineHeight: 1.4 }}>{text}</div>
    </div>
  );
}

function fmtNum(n: number): string {
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toLocaleString('en-US');
}

const sectionLabelStyle: React.CSSProperties = { fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: THEME.textMuted };
const panelStyle: React.CSSProperties = { background: THEME.panelBg, border: THEME.panelBorder, borderRadius: 12, padding: '14px 16px', boxShadow: THEME.panelShadow };
const toolbarLabelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)',
};
const toolbarInputStyle: React.CSSProperties = {
  width: 200, padding: '8px 10px', borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)',
  color: '#fff', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
};
